<template>
  <b-modal
    id="pending-modal"
    ref="modal"
    title="Pending Sessions"
    size="lg"
    hide-footer
  >
    <p v-if="appointmentRequests && appointmentRequests.length">
      Please accept or decline your pending session requests below. If you decline, you will be given the option to message the student on the next screen.
    </p>

    <p
      style="margin-bottom: 45px;"
      v-else
    >
      You currently have no pending sessions
    </p>

    <ul v-if="appointmentRequests && appointmentRequests.length" class="pending-appointment-list mt-4">
      <li v-for="request in appointmentRequests" :class="{finished: accepted[request.id] || rejected[request.id]}">
        <div class="left">
          <label>
            <span v-if="request.existing_patient">EXISTING</span> <span v-else>NEW</span> CLIENT
          </label>
          <h3>{{ request.start_time | dateLongMonthWithDayNoYear }} </h3>
          <span v-if="request.user" class="name"><a target="_blank" :href="`/student/${request.user.id}`">{{request.user.fullName}}</a></span>
          <span v-if="request.school" class="name">{{request.school}}</span>
          <p class='errorMessage' v-if="responseErrors[request.id] === 'pending-payment'">
            This request cannot be approved because the client has a pending payment.
            <span class='pretend-link' @click="rejectBecauseOfPendingPayment(request)">
              Click here to decline the request with a prefilled message.
            </span>
          </p>
          <p class='errorMessage' v-else-if="responseErrors[request.id] === 'session-overlap'">
            This request cannot be approved because you already have a session during this time.
          </p>
          <p class='errorMessage' v-else-if="responseErrors[request.id] === 'workshop-overlap'">
            This request cannot be approved because you already have a workshop during this time.
          </p>
          <p class='errorMessage' v-else-if="responseErrors[request.id] === 'request-overlap'">
            This request cannot be approved because you already have a request during this time. 
            Please decline one of the requests before approving the other.
          </p>
        </div>

        <div class="right" v-if="!accepted[request.id] && !rejected[request.id]">
          <div class="check-btn">
            <button type="button" @click="acceptAppointment(request)" class="top">
              <font-awesome-icon
                v-if="loading[request.id]"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', 'check']"
                class="icon alt"
              />
            </button>
          </div>
          <div class="times-btn">
            <button type="button" @click="rejectAppointment(request)" class="bottom">
              <font-awesome-icon
                v-if="loading[request.id]"
                :spin="true"
                :icon="['fas', 'spinner']"
                class="icon alt"
              />
              <font-awesome-icon
                v-else
                :icon="['fas', 'times']"
                class="icon alt"
              />
            </button>
          </div>
        </div>

        <!-- accepted -->
        <div v-else-if="accepted[request.id]" class="right">
          <div class="check-btn">
            <button type="button" class="top selected">
              <font-awesome-icon
                :icon="['fas', 'check']"
                class="icon alt"
              />
            </button>
          </div>
          <div class="times-btn">
            <button type="button" class="bottom">
              <font-awesome-icon
                :icon="['fas', 'times']"
                class="icon alt"
              />
            </button>
          </div>
        </div>

        <!-- rejected -->
        <div v-else-if="rejected[request.id]" class="right">
          <div class="check-btn">
            <button type="button" class="top">
              <font-awesome-icon
                :icon="['fas', 'check']"
                class="icon alt"
              />
            </button>
          </div>
          <div class="times-btn">
            <button type="button" class="bottom selected">
              <font-awesome-icon
                :icon="['fas', 'times']"
                class="icon alt"
              />
            </button>
          </div>
        </div>
      </li>
    </ul>

    <div v-if="allDone">
      <p v-if="appointmentRequests && appointmentRequests.length">Thanks for reviewing all of your pending session requests!</p>
      <button class="btn btn-ghost btn-wide" @click="hide()">
        Close
      </button>
    </div>

    <reject-pending-modal
      ref="rejectModal"
      :on-reject="onReject" />

  </b-modal>
</template>

<script>
import RejectPendingModal from '@/components/modals/RejectPendingModal.vue';
import CounselorClient from '@/api/CounselorClient';
import SessionClient from '@/api/SessionClient';

export default {
  name: 'PendingModal',
  components: {
    RejectPendingModal,
  },
  props: {
    updatePendingRequestsList: {
      type: Function,
    },
  },
  data() {
    return {
      sessionsClient: new SessionClient(this.$http),
      counselorClient: new CounselorClient(this.$http),
      appointmentRequests: null,
      allDone: false,

      loading: {},
      accepted: {},
      rejected: {},
      responseErrors: {},
    };
  },
  methods: {
    show(appointmentRequests) {
      this.$refs.modal.show();
      this.appointmentRequests = appointmentRequests;

      this.allDone = !this.appointmentRequests || !this.appointmentRequests.length;
    },
    hide() {
      this.$refs.modal.hide();
    },


    async _doSave(request) {
      if (this.loading[request.id]) 
        return;

      this.loading[request.id] = true;
      this.responseErrors[request.id] = undefined;
      this.$forceUpdate();

      try {
        await this.sessionsClient.approveRequest(request.id)
        
        this.loading[request.id] = false;
        this.accepted[request.id] = true;
        this.responseErrors[request.id] = undefined;

        this.allDone = this.appointmentRequests.filter(r => this.accepted[r.id] || this.rejected[r.id]).length === this.appointmentRequests.length;

        this.$forceUpdate();
        this.updatePendingRequestsList();
      } catch(ex) {
        this.loading[request.id] = false;
        if(ex.body && ex.body.errors && ex.body.errors.Session) {
          for(const error of ex.body.errors.Session) {
            if(error === 'Client has pending payments')
              this.responseErrors[request.id] = 'pending-payment';
            else if (error === 'Counselor has a session during this time slot')
              this.responseErrors[request.id] = 'session-overlap';
            else if (error === 'Counselor has a session request during this time slot')
              this.responseErrors[request.id] = 'request-overlap';
            else if (error === 'Counselor has a workshop during this time slot')
              this.responseErrors[request.id] = 'workshop-overlap';
          }
        } else {
          this.$store.commit('setGlobalAlert', {
            msg: 'Failed to respond to request. Please try again!', error: true,
          });
        }

        this.$forceUpdate();
      }
    },
    onReject(request) {
      this.loading[request.id] = false;
      this.rejected[request.id] = true;
      this.responseErrors[request.id] = undefined;

      this.allDone = this.appointmentRequests.filter(r => this.accepted[r.id] || this.rejected[r.id]).length === this.appointmentRequests.length;

      this.$forceUpdate();
      this.updatePendingRequestsList();
    },

    acceptAppointment(request) {
      this._doSave(request, true);
    },
    rejectAppointment(request) {
      this.$refs.rejectModal.show(request);
    },
    rejectBecauseOfPendingPayment(request) {
      const prefilledText = "Unfortunately, due to a pending payment, the system prevents me from approving your session request at this time. To resolve the pending payment, click on your icon on the top right, and select payments it will take you to a page showing your session history. On the session(s) that need payment, you can click on the light blue button that says pay. Here you can use your credits to pay for those session(s). Once you complete that action, you should be able to request another session.";
      this.$refs.rejectModal.show(request, prefilledText);
    },
  },
};
</script>

<style lang="scss">
#pending-modal {
  .pretend-link {
    text-decoration: underline; 
  }
  .pretend-link:hover {
    cursor: pointer;
    color: $yellow;
  }
  .pending-appointment-list {
    list-style-type: none;
    padding-left: 0;

    li {
      @include border-radius($border-radius);

      display:flex;
      border: solid 1px #586877;
      background: #20354a;
      margin-bottom: 10px;

      &:last-of-type {
        margin-bottom: 0;
      }

      @media (max-width: $md-max) {
        flex-direction: column;
        height: auto;
      }

      label {
        color: white;
        margin-bottom: 0;
        line-height: 1;
        font-size: 14px;
        display: inline-block;
        position: relative;
        top: -3px;
      }

      h3 {
        font-variant: $sans;
        color: $green;
        font-size: 28px;
      }

      .name {
        opacity: 0.6;
      }
    }

    .left {
      vertical-align: top;
      display: inline-block;
      width: calc(100% - 50px);
      padding: 30px;

      .errorMessage {
        color: $red;
        margin-top: 10px;
        margin-bottom: 0px;
      }
    }

    .finished {
      opacity: 0.5;
      cursor: default !important;
    }

    .right {
      display:flex;
      flex-direction: column;
      vertical-align: top;
      width: 50px;
      border-left: solid 1px #586877;

      @media (max-width: $md-max) {
        flex-direction: row;
        border-left: none;
        border-top: solid 1px #566877;
        width: 100%;
      }

      @media (min-width: $xl-min) {
        width: 70px;
      }

      .check-btn,
      .times-btn {
        height: 50%;
        font-size: 20px;

        @media (max-width: $md-max) {
          height: 60px;
          width: 50%;
          display: inline-block;
          vertical-align: top;
        }

        button {
          &.selected,
          &:hover {
            background: rgba(white, 0.1);
          }
        }
      }

      .check-btn {
        border-bottom: solid 1px #586877;

        @media (max-width: $md-max) {
          border-bottom: none;
          border-right: solid 1px #586877;
        }

        button {
          color: $green;
        }
      }

      .times-btn {
        button {
          color: $red;
        }
      }

      button {
        display: block;
        background: none;
        border: none;
        width: 100%;
        height: 100%;
      }
    }
  }
}
</style>
